import React, { useMemo, useState } from 'react';
import {
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  Card,
  Chip,
  IconButton,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { TabbableAccordion } from '../common/TabbableAccordion';
import { PencilSimple as Edit2, Trash as Trash2, User as UserIcon } from '@phosphor-icons/react';
import type { Opportunity, OpportunityBoardStatus } from '../../types';

const SPECIAL_WON = '__won__';
const SPECIAL_LOST = '__lost__';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const numericValue = (raw: unknown): number => {
  const parsed = typeof raw === 'string' ? parseFloat(raw) : Number(raw ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

interface OpportunityListViewProps {
  opportunities: Opportunity[];
  statuses: OpportunityBoardStatus[];
  onMoveToStatus: (opportunityId: number, statusId: number) => Promise<void> | void;
  onMarkWon: (opportunity: Opportunity) => void;
  onMarkLost: (opportunity: Opportunity) => void;
  onEdit: (opportunity: Opportunity) => void;
  onDelete: (opportunityId: number) => Promise<void> | void;
}

export const OpportunityListView: React.FC<OpportunityListViewProps> = ({
  opportunities,
  statuses,
  onMoveToStatus,
  onMarkWon,
  onMarkLost,
  onEdit,
  onDelete,
}) => {
  // Track which row is currently mid-update so the Select disables during the API call.
  const [pendingMoveId, setPendingMoveId] = useState<number | null>(null);

  const sortedStatuses = useMemo(
    () => [...statuses].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [statuses]
  );

  const groupedOpportunities = useMemo(() => {
    const groups = new Map<number, Opportunity[]>();
    sortedStatuses.forEach((s) => groups.set(s.id, []));
    opportunities.forEach((opp) => {
      // Closed (won/lost) deals stay in their stage column — the caller decides whether
      // they are included at all via the Show Won / Show Lost filters.
      if (opp.board_status == null) return;
      const bucket = groups.get(opp.board_status);
      if (bucket) bucket.push(opp);
    });
    return groups;
  }, [opportunities, sortedStatuses]);

  const handleStageChange = async (
    opportunity: Opportunity,
    newValue: string | null
  ) => {
    if (newValue == null) return;
    if (newValue === SPECIAL_WON) {
      onMarkWon(opportunity);
      return;
    }
    if (newValue === SPECIAL_LOST) {
      onMarkLost(opportunity);
      return;
    }
    const targetStatusId = Number(newValue);
    if (!Number.isFinite(targetStatusId) || targetStatusId === opportunity.board_status) {
      return;
    }
    setPendingMoveId(opportunity.id);
    try {
      await onMoveToStatus(opportunity.id, targetStatusId);
    } finally {
      setPendingMoveId(null);
    }
  };

  if (sortedStatuses.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography level="body-md" textColor="text.secondary">
          No board selected or no statuses available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <AccordionGroup size="sm" disableDivider sx={{ gap: 1.5 }}>
        {sortedStatuses.map((status) => {
          const items = groupedOpportunities.get(status.id) || [];
          const totalValue = items.reduce(
            (sum, opp) => sum + numericValue(opp.estimated_value ?? opp.value),
            0
          );

          return (
            <TabbableAccordion key={status.id} defaultExpanded={items.length > 0}>
              <AccordionSummary>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Typography level="title-sm" sx={{ minWidth: 0 }}>
                    {status.name}
                  </Typography>
                  <Chip size="sm" variant="soft">
                    {items.length}
                  </Chip>
                  {totalValue > 0 && (
                    <Typography level="body-xs" textColor="text.secondary">
                      {formatCurrency(totalValue)}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {items.length === 0 ? (
                  <Typography
                    level="body-sm"
                    textColor="text.secondary"
                    sx={{ py: 1, px: 1 }}
                  >
                    No opportunities in this stage.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {items.map((opp) => {
                      const value = numericValue(opp.estimated_value ?? opp.value);
                      return (
                        <Card
                          key={opp.id}
                          variant="outlined"
                          sx={{ p: 1.5, gap: 1 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              minWidth: 0,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                level="title-sm"
                                sx={{ wordBreak: 'break-word' }}
                                endDecorator={
                                  opp.is_closed ? (
                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      color={opp.is_won ? 'success' : 'danger'}
                                    >
                                      {opp.is_won ? 'Won' : 'Lost'}
                                    </Chip>
                                  ) : null
                                }
                              >
                                {opp.name}
                              </Typography>
                              {opp.contact_name && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 0.25,
                                  }}
                                >
                                  <UserIcon size={12} />
                                  <Typography
                                    level="body-xs"
                                    textColor="text.secondary"
                                  >
                                    {opp.contact_name}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ flexShrink: 0 }}
                            >
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={() => onEdit(opp)}
                                aria-label="Edit opportunity"
                              >
                                <Edit2 size={16} />
                              </IconButton>
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={() => onDelete(opp.id)}
                                aria-label="Delete opportunity"
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </Stack>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'stretch', sm: 'center' },
                              justifyContent: 'space-between',
                              gap: 1,
                            }}
                          >
                            <Typography
                              level="title-sm"
                              textColor="success.plainColor"
                            >
                              {formatCurrency(value)}
                              {opp.probability > 0 && opp.probability < 100 && (
                                <Typography
                                  level="body-xs"
                                  component="span"
                                  textColor="text.tertiary"
                                  sx={{ ml: 0.5 }}
                                >
                                  ({opp.probability}%)
                                </Typography>
                              )}
                            </Typography>

                            <Select
                              size="sm"
                              value={opp.board_status != null ? String(opp.board_status) : null}
                              onChange={(_, newValue) => handleStageChange(opp, newValue)}
                              disabled={pendingMoveId === opp.id}
                              sx={{ minWidth: { xs: '100%', sm: 200 } }}
                            >
                              {sortedStatuses.map((s) => (
                                <Option key={s.id} value={String(s.id)}>
                                  {s.name}
                                </Option>
                              ))}
                              {!opp.is_closed && <Option value={SPECIAL_WON}>Mark as Won</Option>}
                              {!opp.is_closed && <Option value={SPECIAL_LOST}>Mark as Lost</Option>}
                            </Select>
                          </Box>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </AccordionDetails>
            </TabbableAccordion>
          );
        })}
      </AccordionGroup>
    </Box>
  );
};
